import type { Payments_PaymentProcessorId as PaymentProcessorId } from '__generated__/graphql-types';

import isMobileApp from 'js/lib/isMobileApp';
import requestCountry from 'js/lib/requestCountry';
import { get as getUser } from 'js/lib/user';

import { useProductFeatures } from 'bundles/enroll-common/utils/freeTrialExperimentProductFeatureFlags';
import type { PaymentProcessorType } from 'bundles/payments-common/common/PaymentProcessorTypes';
import { IdTypes } from 'bundles/product-features';
import { isStripePaymentProcessor } from 'bundles/subscriptions-common/utils/isStripePaymentProcessor';

export type Variant = 'control' | 'revoke-access' | 'limited-free-trial' | 'revoke-and-limited-trial';

const SHIPPED_VARIANT: Variant = 'revoke-and-limited-trial';

// The revoke-and-limited-trial variant is GA'd; the exclusion checks below still return 'control', and useIsLimitedFreeTrialEnabled also excludes non-Stripe checkouts.
export const useRevokeAccessAndLimitedTrialVariant = (options: {
  variantOnFailure: Variant;
  shouldRecordExposure?: boolean;
}): { variant: Variant; loading: boolean } => {
  const { variantOnFailure } = options;
  const shouldRecordExposure =
    options?.shouldRecordExposure !== undefined && typeof options?.shouldRecordExposure === 'boolean'
      ? options.shouldRecordExposure
      : true;
  const userId = getUser().id;
  const { features, loading, error } = useProductFeatures({
    idType: IdTypes.User,
    id: String(userId),
    skip: !shouldRecordExposure || !userId,
  });
  const isExcludedFromExperiment = features.get(
    'commerceMonetization',
    'excludeLearnerSubscribedPriorToCurrentMonetizationExperiment'
  );
  if (loading) {
    return { variant: 'control', loading: true };
  }
  if (isExcludedFromExperiment) {
    return { variant: 'control', loading: false };
  }
  // Mobile is excluded from FE exposure only (MONETIZE-3163); BE item locking still
  // evaluates Statsig once mobile targeting is removed from the experiment gate.
  if (isMobileApp.get()) {
    return { variant: 'control', loading: false };
  }
  if (!shouldRecordExposure) {
    return { variant: 'control', loading: false };
  }
  // An errored exclusion check would otherwise read as a control assignment.
  if (error) {
    return { variant: variantOnFailure, loading: false };
  }
  return { variant: SHIPPED_VARIANT, loading: false };
};

export const useIsRevokeAccessEnabled = (
  shouldRecordExposure: boolean = true
): { isRevokeAccessEnabled: boolean; loading: boolean } => {
  // Fail open: an unresolved variant must not revoke access from a true control learner.
  const { variant, loading } = useRevokeAccessAndLimitedTrialVariant({
    variantOnFailure: 'control',
    shouldRecordExposure,
  });
  return { isRevokeAccessEnabled: variant === 'revoke-access' || variant === 'revoke-and-limited-trial', loading };
};

// Before selection, disable only when all offered processors are non-Stripe so legal copy cannot flip; non-Stripe cannot convert the trial to paid early.
export const useIsLimitedFreeTrialEnabled = (options?: {
  paymentProcessor?: PaymentProcessorType | null;
  paymentProcessors?: ReadonlyArray<PaymentProcessorId> | null;
  shouldRecordExposure?: boolean;
}): { isLimitedFreeTrialEnabled: boolean; loading: boolean } => {
  // Fail closed: an unresolved variant locks transiently rather than promising unlimited access.
  const { variant, loading } = useRevokeAccessAndLimitedTrialVariant({
    variantOnFailure: 'limited-free-trial',
    shouldRecordExposure: options?.shouldRecordExposure,
  });
  const paymentProcessor = options?.paymentProcessor;
  const paymentProcessors = options?.paymentProcessors;
  // India has no Stripe checkout rail, so it is always non-Stripe regardless of the offered processors.
  const isNonStripeCheckout =
    requestCountry.get() === 'IN' ||
    (paymentProcessors?.length
      ? paymentProcessors.every((processor) => !isStripePaymentProcessor(processor))
      : Boolean(paymentProcessor && !isStripePaymentProcessor(paymentProcessor)));
  return {
    isLimitedFreeTrialEnabled:
      !isNonStripeCheckout && (variant === 'limited-free-trial' || variant === 'revoke-and-limited-trial'),
    loading,
  };
};
